/**
 * "Topla" (design §4): presentational bounding-box shelf packing so the whole
 * set is visible at once — deliberately NOT the nesting engine. Groups by
 * material, keeps every piece at rotation 0, and answers a pose per key.
 */
import type { PiecePose } from '../../../../worker/api/saya.contract'
import type { Bounds } from '../geometry'

export interface TidyPiece {
  key: string
  bounds: Bounds
  materialId: number | null
}

export interface TidyOptions {
  gutterMm: number
  groupGapMm: number
  rowWidthMm: number
}

export const TIDY_DEFAULTS: TidyOptions = { gutterMm: 8, groupGapMm: 30, rowWidthMm: 700 }

export function tidyLayout(
  pieces: readonly TidyPiece[],
  opts: TidyOptions = TIDY_DEFAULTS,
): Map<string, PiecePose> {
  const poses = new Map<string, PiecePose>()
  const groups = new Map<string, TidyPiece[]>()
  for (const p of pieces) {
    // A leading space cannot collide with String(materialId) for any number.
    const groupKey = p.materialId === null ? ' null' : String(p.materialId)
    const list = groups.get(groupKey)
    if (list) list.push(p)
    else groups.set(groupKey, [p])
  }
  // Nulls last, everything else in first-seen order.
  const ordered = [...groups.entries()].sort((a, b) =>
    a[0] === ' null' ? 1 : b[0] === ' null' ? -1 : 0,
  )

  let shelfY = 0
  for (const [, group] of ordered) {
    let cursorX = 0
    let shelfH = 0
    for (const p of group) {
      const w = p.bounds.maxX - p.bounds.minX
      const h = p.bounds.maxY - p.bounds.minY
      if (cursorX > 0 && cursorX + w > opts.rowWidthMm) {
        shelfY += shelfH + opts.gutterMm
        cursorX = 0
        shelfH = 0
      }
      poses.set(p.key, { rotDeg: 0, txMm: cursorX - p.bounds.minX, tyMm: shelfY - p.bounds.minY })
      cursorX += w + opts.gutterMm
      shelfH = Math.max(shelfH, h)
    }
    shelfY += shelfH + opts.groupGapMm
  }
  return poses
}
